import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';

import config from '@/config';
import ArtlistDataContext from '@/context/ArtlistDataContext';
import useCache from '@/hooks/useCache';
import useStateHandler from '@/hooks/useStateHandler';
import GoBackButton from '@/shared-components/buttons/components/GoBackButton';
import Loader from '@/shared-components/loader/Loader';

import ArtlistHeader from './components/ArtlistHeader';
import ArtlistNotFound from './components/ArtlistNotFound';
import ArtlistBody from './artlist-body';

import './index.css';

export default function Artlist() {
  const { state } = useStateHandler();
  const { artlistId } = useParams();
  const { loading, fetchData, setFetchData } = useCache(
    `artlist_${artlistId}_view`,
    `${config.apis.api.url}/artlist/${artlistId}`,
    {
      injectToken: true,
      invalidateWhen: ["ADD_TO_ARTLIST_FROM_ARTWORK", "DELETE_ARTLIST"],
    }
  );

  const [metaData, setMetaData] = useState({ title: "", metadesc: "", canonical: "" });

  useEffect(() => {
    if (fetchData) {
      const {
        _id: artlistId,
        artlist_title,
        artlist_creator: { user_username },
        artlist_artworks
      } = fetchData;

      const title = `Artlist | ${artlist_title} by ${user_username} on Suarte`;
      const canonical = `https://suarte.art/artlist/${artlistId}`;
      const metadesc = `Discover ${user_username}’s artlist containing ${artlist_artworks.length} artworks. View and play the artlist on Suarte.`;

      setMetaData({ title, metadesc, canonical });
    }
  }, [fetchData]);

  if (loading) {
    return (
      <>
        <GoBackButton />
        <div className="artlist-page__loading-page">
          <Loader className="artlist-page__spinner" thin />
        </div>
      </>
    );
  } else if (fetchData.error_type === "NOT_FOUND") {
    return <ArtlistNotFound />;
  } else {
    return (
      <>
        <Helmet>
          <title>{metaData.title}</title>
          <link rel="canonical" href={metaData.canonical} />
          <meta name="description" content={metaData.metadesc} />
        </Helmet>
        <ArtlistDataContext.Provider
          value={{
            artlistData: fetchData,
            setArtlistData: setFetchData,
            isCreator: state.user_session?._id === fetchData.artlist_creator._id,
          }}
        >
          <div className="artlist-page">
            <GoBackButton />

            <div className="artlist-page__container-wrap">
              <ArtlistHeader key={fetchData.artlist_image?.image_id} />

              <ArtlistBody cacheKey={`artlist_${artlistId}_view`} />
            </div>
          </div>
        </ArtlistDataContext.Provider>
      </>
    );
  }
}
